import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { ISavingsBucket } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IRolloverBucketRequest {
  savingsAccountNumber: string;
  rolloverOption: boolean;
}

interface IRolloverBucketResponse {
  status?: boolean;
  message?: string;
  data?: ISavingsBucket;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IRolloverBucketResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const rolloverBucketAsync = createAsyncThunk(
  'bucket/rollover',
  async (
    values: IRolloverBucketRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<IRolloverBucketResponse>(
        '/savings/accounts/rollover-options',
        values,
        getSavingsAuthConfig(state),
      );

      dispatch(
        showAlert({
          status: 'success',
          description:
            data?.message ?? 'Bucket has been rolled over successfully',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IRolloverBucketResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketRolloverSlice = createSlice({
  name: 'bucket/rollover',
  initialState,
  reducers: {
    resetBucketRollover(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rolloverBucketAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(rolloverBucketAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IRolloverBucketResponse;
      })
      .addCase(rolloverBucketAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IRolloverBucketResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetBucketRollover } = bucketRolloverSlice.actions;

export default bucketRolloverSlice.reducer;
